<script lang="ts">
	import type { DateValue } from '@internationalized/date';
	import type { CalendarEvent } from '@/types/calendar';
	import CalendarEventComponent from '../CalendarEvent.svelte';
	import * as ScrollArea from '@/components/ui/scroll-area';
	import {
		getWeekDates,
		formatDayOfWeek,
		formatDayNumber,
		isToday,
		getEventsForDate,
	} from '../calendar.utils';
	import { cn } from '@/lib/utils';

	interface Props {
		currentDate: DateValue;
		events: CalendarEvent[];
		onEventClick?: (event: CalendarEvent) => void;
		showPopover?: boolean;
	}

	let {
		currentDate,
		events,
		onEventClick,
		showPopover = true,
	}: Props = $props();

	// Get the 7 days for the current week
	const weekDates = $derived(getWeekDates(currentDate));

	// Get events grouped by day
	const eventsByDay = $derived.by(() => {
		return weekDates.map((date) => ({
			date,
			events: getEventsForDate(events, date),
		}));
	});
</script>

<div class="flex h-full flex-col rounded-lg border">
	<!-- Sticky day headers -->
	<div
		class="sticky z-30 grid grid-cols-7 border-b"
		style="top: var(--view-sticky-top, 0px)"
	>
		{#each eventsByDay as { date }, index (`header-${date.year}-${date.month}-${date.day}`)}
			{@const isCurrentDay = isToday(date)}
			{@const isLastColumn = index === 6}
			<div
				class={cn(
					'flex flex-col items-center px-2 py-3',
					!isLastColumn && 'border-r',
					isCurrentDay ? 'bg-accent text-accent-foreground' : 'bg-muted/40',
				)}
			>
				<span class="text-xs font-medium uppercase tracking-wide">
					{formatDayOfWeek(date)}
				</span>
				<span
					class={cn(
						'mt-1 flex size-8 items-center justify-center rounded-full text-sm font-semibold',
						isCurrentDay && 'bg-primary text-primary-foreground',
					)}
				>
					{formatDayNumber(date)}
				</span>
			</div>
		{/each}
	</div>

	<!-- Day columns with events -->
	<div class="grid flex-1 grid-cols-7">
		{#each eventsByDay as { date, events: dayEvents }, index (`${date.year}-${date.month}-${date.day}`)}
			{@const isCurrentDay = isToday(date)}
			{@const isLastColumn = index === 6}
			<div
				class={cn(
					'flex min-h-0 flex-col',
					!isLastColumn && 'border-r',
					isCurrentDay && 'bg-accent/10',
				)}
			>
				<ScrollArea.Root class="flex-1" orientation="vertical">
					<div class="space-y-2 p-2">
						{#each dayEvents as event (event.id)}
							<CalendarEventComponent
								{event}
								compact
								{showPopover}
								onclick={() => onEventClick?.(event)}
								class="text-sm"
							/>
						{/each}
					</div>
				</ScrollArea.Root>
			</div>
		{/each}
	</div>
</div>